/* @jsxImportSource react */
'use client';

import React from 'react';

import { type LinkRenderProps, useLinkRenderer } from './link-provider';
import { type StripUndesiredProps } from './strip-undesired-props';

type ContainerElement =
  | 'div'
  | 'aside'
  | 'header'
  | 'footer'
  | 'nav'
  | 'main'
  | 'section'
  | 'article';

type SemanticContainerProps = Omit<
  StripUndesiredProps<React.HTMLAttributes<HTMLElement>>,
  'onClick'
> & {
  /**
   * Semantic element to render. Mutually exclusive with `onClick` and `href`.
   * @default "div"
   */
  as?: ContainerElement;
  children?: React.ReactNode;
  onClick?: never;
  href?: never;
};

type ButtonContainerProps = Omit<
  StripUndesiredProps<React.ButtonHTMLAttributes<HTMLButtonElement>>,
  'aria-label'
> & {
  /**
   * Click handler. Presence of `onClick` renders the container as
   * `<button type="button">` and requires `aria-label`. Mutually exclusive
   * with `as` and `href`.
   */
  onClick: React.MouseEventHandler<HTMLButtonElement>;
  /** Required when the container is interactive — names the action for assistive tech. */
  'aria-label': string;
  children?: React.ReactNode;
  as?: never;
  href?: never;
};

type AnchorContainerProps = Omit<StripUndesiredProps<LinkRenderProps>, 'aria-label' | 'href'> & {
  /**
   * Navigation target. Presence of `href` renders the container as `<a>` via
   * the configured `LinkProvider` (so framework routing works) and requires
   * `aria-label`. Mutually exclusive with `as` and `onClick`-only mode.
   */
  href: string;
  /** Required when the container is interactive — names the destination for assistive tech. */
  'aria-label': string;
  /** When `true`, opens in a new tab with `rel="noopener noreferrer"`. */
  external?: boolean;
  children?: React.ReactNode;
  as?: never;
};

export type ContainerProps = SemanticContainerProps | ButtonContainerProps | AnchorContainerProps;

export const Container = React.forwardRef<HTMLElement, ContainerProps>((props, ref) => {
  const renderLink = useLinkRenderer();

  if (props.href !== undefined) {
    const { children, external, as: _as, href, ...rest } = props;
    const externalProps = external ? { target: '_blank', rel: 'noopener noreferrer' } : {};
    // Call the configured renderer (see RenderLink) rather than rendering a
    // context-provided component, so the anchor's element type stays static.
    return renderLink({
      // eslint-disable-next-line typescript/no-unsafe-type-assertion -- forwardRef gives Ref<HTMLElement>; this branch only ever renders an anchor, so narrowing the ref is sound
      ref: ref as React.Ref<HTMLAnchorElement>,
      href,
      to: href,
      ...externalProps,
      ...rest,
      children,
    });
  }

  if (props.onClick) {
    const { children, as: _as, href: _href, ...rest } = props;
    // eslint-disable-next-line typescript/no-unsafe-type-assertion -- forwardRef gives Ref<HTMLElement>; this branch renders a <button>, so narrowing the ref is sound
    const buttonRef = ref as React.Ref<HTMLButtonElement>;
    return (
      // eslint-disable-next-line atoms/no-raw-elements -- Box/Grid render a <button> here when given onClick; this is the layout primitives' button implementation
      <button type="button" ref={buttonRef} {...rest}>
        {children}
      </button>
    );
  }

  const { as: Component = 'div', children, onClick: _onClick, href: _href, ...rest } = props;
  // eslint-disable-next-line typescript/no-unsafe-type-assertion -- forwardRef gives Ref<HTMLElement>; the rendered semantic element extends HTMLElement, so narrowing the ref is sound
  const componentRef = ref as React.Ref<HTMLDivElement>;
  return (
    <Component ref={componentRef} {...rest}>
      {children}
    </Component>
  );
});

Container.displayName = 'Container';
